import React from 'react'
import { dayjs } from '@mobi/utils/date'
import { CountdownBadge } from '@mobi/component-library/Feedback/CountdownBadge'
import type { BetLoopKambiShareCoupon } from '@core/Areas/BetLoop/types'
import { StatusBadge } from '@mobi/component-library/Common/StatusBadge'

export const CountdownStatus: React.FC<{
  eventStartTimes: string
  betStatus:
    | BetLoopKambiShareCoupon['bets'][number]['betStatus']
    | BetLoopKambiShareCoupon['couponRows'][0]['status']
}> = ({ eventStartTimes, betStatus }) => {
  const eventStatus = getEventState(eventStartTimes, betStatus)

  if (eventStatus === 'pre-live') {
    const countdownDate = getCountdownDate(eventStartTimes)
    const counterDateObj = new Date(countdownDate)

    return countdownDate && <CountdownBadge badgeSize='S' advertisedStartTime={counterDateObj} />
  }

  if (eventStatus === 'won' || eventStatus === 'no-payout' || eventStatus === 'refunded') {
    return (
      <StatusBadge badgeSize='S' color={eventStatus === 'won' ? 'success' : 'neutral'}>
        {toProperCase(eventStatus)}
      </StatusBadge>
    )
  }

  if (eventStatus === 'pending') {
    return <StatusBadge badgeSize='S'>Pending</StatusBadge>
  }

  const countdownDate = getCountdownDate(eventStartTimes)
  return (
    countdownDate && <CountdownBadge badgeSize='S' advertisedStartTime={new Date(countdownDate)} />
  )
}

// =======
// Helpers
// =======

function getCountdownDate(eventStartDates: string): string {
  return dayjs(eventStartDates).toISOString()
}

function toProperCase(str: string): string {
  if (str === 'no-payout') return 'No-Payout'
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function getEventState(
  eventStartDate: string,
  betStatus:
    | BetLoopKambiShareCoupon['bets'][number]['betStatus']
    | BetLoopKambiShareCoupon['couponRows'][0]['status']
): CountdownStatus {
  const now = dayjs()
  const startDate = dayjs(eventStartDate)

  if (now.isBefore(startDate) && (betStatus === 'OPEN' || betStatus === 'PENDING_LIVE_BET')) {
    return 'pre-live'
  }

  if (betStatus === 'WON') {
    return 'won'
  }

  if (betStatus === 'LOST') {
    return 'no-payout'
  }

  if (betStatus === 'VOID') {
    return 'refunded'
  }

  return 'pending'
}

// =======
// Types
// =======

type CountdownStatus = 'pre-live' | 'pending' | 'live' | 'won' | 'no-payout' | 'refunded'
